import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * AI Chat Widget - "Denise" AI Assistant
 * Floating chat interface for AI-powered support
 */
export default function AIChatWidget({ propertyId = null, tenancyId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [caseStatus, setCaseStatus] = useState(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'ai',
        content: "Hi! I'm Denise, your AI property assistant. I can help you with:\n\n• Maintenance issues\n• Rent & payment queries\n• Deposit questions\n• Tenancy information\n• General property questions\n\nHow can I help you today?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      let response;
      
      if (currentCaseId) {
        // Continue existing conversation
        response = await api.post(`/ai-cases/${currentCaseId}/message`, {
          message: inputValue
        });
      } else {
        // Start new case
        response = await api.post('/ai-cases', {
          subject: inputValue.substring(0, 50),
          message: inputValue,
          property_id: propertyId,
          tenancy_id: tenancyId
        });
        setCurrentCaseId(response.data.case_id);
      }

      const aiMessage = {
        role: 'ai',
        content: response.data.ai_response || response.data.response,
        timestamp: new Date().toISOString(),
        resolved: response.data.resolved,
        action: response.data.action_taken
      };

      setMessages(prev => [...prev, aiMessage]);
      setCaseStatus(response.data.case_status);

      // Show resolution notification
      if (response.data.resolved) {
        toast.success('Issue resolved! Feel free to ask if you need anything else.');
      }

      // Show escalation notification
      if (response.data.action_taken === 'escalate_human') {
        toast.info('Connecting you with our support team...');
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, I had trouble processing that. Please try again or contact support.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setCurrentCaseId(null);
    setCaseStatus(null);
    setIsOpen(true);
  };

  const rateConversation = async (rating) => {
    if (!currentCaseId) return;
    
    try {
      await api.post(`/ai-cases/${currentCaseId}/rate`, { rating });
      toast.success('Thanks for your feedback!');
    } catch (error) {
      console.error('Rating error:', error);
    }
  };

  const escalateToHuman = async () => {
    if (!currentCaseId) return;
    
    try {
      await api.post(`/ai-cases/${currentCaseId}/escalate`);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Your case has been escalated to our support team. Someone will respond shortly.',
        timestamp: new Date().toISOString()
      }]);
      setCaseStatus('escalated');
      toast.info('Case escalated to support team');
    } catch (error) {
      toast.error('Could not escalate. Please try again.');
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center z-50"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Denise</h3>
                <p className="text-xs text-white/80">AI Property Assistant</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${
                  msg.role === 'user' 
                    ? 'bg-primary text-white rounded-2xl rounded-br-md' 
                    : msg.role === 'system'
                    ? 'bg-amber-100 text-amber-800 rounded-2xl'
                    : 'bg-white border border-slate-200 rounded-2xl rounded-bl-md'
                } px-4 py-2.5 shadow-sm`}>
                  {msg.role === 'ai' && (
                    <div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
                      <Bot className="w-3 h-3" />
                      <span>Denise</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  
                  {/* Resolution indicator */}
                  {msg.resolved && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3" />
                      <span>Issue resolved</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Denise is typing...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Actions bar (when case exists) */}
          {currentCaseId && caseStatus !== 'escalated' && (
            <div className="px-4 py-2 bg-slate-100 border-t flex items-center justify-between text-xs">
              <button 
                onClick={escalateToHuman}
                className="text-slate-600 hover:text-primary flex items-center gap-1"
              >
                <AlertTriangle className="w-3 h-3" />
                Talk to a human
              </button>
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Helpful?</span>
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    onClick={() => rateConversation(rating)}
                    className="w-6 h-6 rounded hover:bg-slate-200 text-slate-400 hover:text-yellow-500"
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isLoading || caseStatus === 'escalated'}
              />
              <Button 
                onClick={sendMessage} 
                disabled={!inputValue.trim() || isLoading}
                size="icon"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {caseStatus === 'escalated' && (
              <p className="text-xs text-amber-600 mt-2">
                Your case is with our support team. They&apos;ll respond soon.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
